/**
 * Palette arming store (Sprint 4.3.7-fix).
 *
 * Owns the click-to-add interaction state for the Pathway Palette /
 * Canvas pair. The founder direction (2026-05-08) was to abandon the
 * Sprint 4.3.7 HTML5 drag-and-drop flow and switch to a click-to-add
 * pattern (mirrors Figma / Miro). The drag flow's unit tests passed but
 * end-to-end Playwright drives proved that real `mouse.down/move/up`
 * events do not trigger HTML5 `dragstart` the way React Flow expects.
 *
 * Interaction:
 *   1. User clicks a palette row -> store flips to `armedNodeType: <type>`
 *      + (for activity types) `armedActivityType: <activity>`. Palette
 *      row paints an "armed" visual; canvas wrapper paints `cursor:
 *      crosshair`.
 *   2. User clicks anywhere on the canvas -> editor reads the armed
 *      type, calls `addNode` with the click coordinate, then `disarm()`s
 *      the store so the next click is a normal selection.
 *   3. User presses ESC OR clicks the same palette row a second time ->
 *      `disarm()`. ESC handler is wired in the editor view.
 *
 * Backwards compat: HTML5 drag-and-drop continues to work alongside
 * click-to-add (PathwayPalette still wires `dragstart`). Existing unit
 * tests that fire `dragStart` + `drop` events keep passing. Playwright
 * (real-mouse-events) tests use the click flow.
 *
 * HC1 reproducibility: in-memory only. No persistence. Tests reset state
 * with `usePaletteArmingStore.setState({ armedNodeType: null,
 * armedActivityType: null })` in `beforeEach`.
 *
 * HC5: zero em / en dashes.
 * HC6 a11y: the click-to-add pattern is keyboard-accessible by default
 * since palette rows are real `<button>` elements; ESC disarms.
 *
 * HC9 strict registry: arming itself does NOT emit telemetry (the user
 * has merely picked up an item). The downstream `addNode` call still
 * emits `pathway-add-node` so the AuditEvent stream is unchanged.
 */
import { create } from 'zustand';
import type { PathwayNodeType } from '../services/pathway-layout';

export interface PaletteArmingState {
  /** Currently armed node type, or null when no item is armed. */
  armedNodeType: PathwayNodeType | null;
  /**
   * For activity-type drags this carries the source activity slug
   * (demographics / vitals / labs / etc). The editor uses it to resolve
   * the action `category` per Awell ontology.
   */
  armedActivityType: PathwayNodeType | null;
  /** Arm a hierarchy or activity type. Toggles off when same type clicked twice. */
  arm: (nodeType: PathwayNodeType, activityType?: PathwayNodeType) => void;
  /** Disarm. Called by ESC, by the canvas after a successful drop, or programmatically. */
  disarm: () => void;
}

export const usePaletteArmingStore = create<PaletteArmingState>((set, get) => ({
  armedNodeType: null,
  armedActivityType: null,
  arm: (nodeType, activityType) => {
    const { armedNodeType: currentNode, armedActivityType: currentActivity } =
      get();
    // Toggle off when the user clicks the same row a second time.
    if (
      currentNode === nodeType &&
      (currentActivity ?? null) === (activityType ?? null)
    ) {
      set({ armedNodeType: null, armedActivityType: null });
      return;
    }
    set({
      armedNodeType: nodeType,
      armedActivityType: activityType ?? null,
    });
  },
  disarm: () => {
    if (
      get().armedNodeType !== null ||
      get().armedActivityType !== null
    ) {
      set({ armedNodeType: null, armedActivityType: null });
    }
  },
}));
